// Pure geometry for glyph.shape@1 and glyph.iso@1, in a 100×100 box centred on (50, 50).

import type { ShapeName } from "./types";

export const BOX = 100;
const C = BOX / 2;

type Pt = [number, number];

const f = (n: number) => Math.round(n * 100) / 100;
const poly = (pts: Pt[]) => `M${pts.map(([x, y]) => `${f(x)},${f(y)}`).join(" L")} Z`;

function regular(n: number, radius: number, cy = C, startDeg = -90): Pt[] {
  return Array.from({ length: n }, (_, i) => {
    const a = ((startDeg + (360 / n) * i) * Math.PI) / 180;
    return [C + radius * Math.cos(a), cy + radius * Math.sin(a)] as Pt;
  });
}

function rotatePts(pts: Pt[], deg: number): Pt[] {
  const a = (deg * Math.PI) / 180;
  const cos = Math.cos(a);
  const sin = Math.sin(a);
  return pts.map(([x, y]) => [C + (x - C) * cos - (y - C) * sin, C + (x - C) * sin + (y - C) * cos]);
}

function plusPts(r: number, w: number): Pt[] {
  return [
    [C - w, C - r],
    [C + w, C - r],
    [C + w, C - w],
    [C + r, C - w],
    [C + r, C + w],
    [C + w, C + w],
    [C + w, C + r],
    [C - w, C + r],
    [C - w, C + w],
    [C - r, C + w],
    [C - r, C - w],
    [C - w, C - w],
  ];
}

const circlePath = (r: number) => `M${C - r},${C} a${r},${r} 0 1,0 ${2 * r},0 a${r},${r} 0 1,0 ${-2 * r},0 Z`;

/**
 * SVG path for a shape of radius `r` (half the nominal size). Arrow points up at
 * rotation 0; rotation is applied by the caller (degrees clockwise).
 * `evenOdd` marks paths with holes (ring).
 */
export function shapePath(shape: ShapeName | string, r: number): { d: string; evenOdd?: boolean } | null {
  switch (shape) {
    case "circle":
      return { d: circlePath(r) };
    case "square": {
      const h = r * 0.86;
      return {
        d: poly([
          [C - h, C - h],
          [C + h, C - h],
          [C + h, C + h],
          [C - h, C + h],
        ]),
      };
    }
    case "triangle":
      return { d: poly(regular(3, r * 1.12, C + r * 0.14)) };
    case "star": {
      const pts: Pt[] = Array.from({ length: 10 }, (_, i) => {
        const a = ((-90 + 36 * i) * Math.PI) / 180;
        const rr = i % 2 === 0 ? r * 1.06 : r * 0.46;
        return [C + rr * Math.cos(a), C + r * 0.06 + rr * Math.sin(a)];
      });
      return { d: poly(pts) };
    }
    case "diamond":
      return {
        d: poly([
          [C, C - r],
          [C + r * 0.74, C],
          [C, C + r],
          [C - r * 0.74, C],
        ]),
      };
    case "hexagon":
      return { d: poly(regular(6, r)) };
    case "pentagon":
      return { d: poly(regular(5, r * 1.04, C + r * 0.08)) };
    case "plus":
      return { d: poly(plusPts(r, r * 0.3)) };
    case "cross":
      return { d: poly(rotatePts(plusPts(r * 1.05, r * 0.28), 45)) };
    case "ring":
      return { d: `${circlePath(r)} ${circlePath(r * 0.56)}`, evenOdd: true };
    case "arrow":
      return {
        d: poly([
          [C, C - r],
          [C + r * 0.82, C - r * 0.08],
          [C + r * 0.26, C - r * 0.08],
          [C + r * 0.26, C + r],
          [C - r * 0.26, C + r],
          [C - r * 0.26, C - r * 0.08],
          [C - r * 0.82, C - r * 0.08],
        ]),
      };
    case "heart": {
      const p = (x: number, y: number) => `${f(C + x * r)},${f(C + 0.06 * r + y * r)}`;
      return {
        d:
          `M${p(0, 0.9)} C${p(-0.12, 0.8)} ${p(-1, 0.3)} ${p(-1, -0.28)} ` +
          `C${p(-1, -0.78)} ${p(-0.36, -0.98)} ${p(0, -0.52)} ` +
          `C${p(0.36, -0.98)} ${p(1, -0.78)} ${p(1, -0.28)} ` +
          `C${p(1, 0.3)} ${p(0.12, 0.8)} ${p(0, 0.9)} Z`,
      };
    }
    default:
      return null;
  }
}

export type IsoFaceName = "top" | "left" | "right";

export interface IsoFace {
  key: string;
  face: IsoFaceName;
  /** The voxel this face belongs to. */
  voxel: readonly [number, number, number];
  points: string;
}

export interface IsoLayout {
  faces: IsoFace[];
  /** Box units per voxel edge after fitting (for stroke widths). */
  unit: number;
}

type Voxel = readonly [number, number, number];

const COS30 = Math.cos(Math.PI / 6);

/**
 * Isometric projection used by glyph.iso@1 and the boards (15 Puzzle Types -
 * Spatial, `iso()`): x runs to the screen's lower right, y to the lower left,
 * z straight up, viewed from +x +y +z. So the visible faces are
 *   top   = the +z face (color.puzzle.voxelTop),
 *   left  = the +y face (voxelLeft),
 *   right = the +x face (voxelRight).
 */
export function isoProject(x: number, y: number, z: number): Pt {
  return [(x - y) * COS30, (x + y) * 0.5 - z];
}

/**
 * Back-to-front (painter's) order, as on the boards: by x + y, then z, then x.
 * For unit voxels on the integer lattice this never draws a nearer voxel
 * (larger x + y + z) before a farther one whose silhouette it overlaps.
 */
export function isoOrder(a: Voxel, b: Voxel): number {
  return a[0] + a[1] - (b[0] + b[1]) || a[2] - b[2] || a[0] - b[0];
}

function validVoxels(voxels: readonly (readonly unknown[])[]): Voxel[] {
  const seen = new Set<string>();
  const out: Voxel[] = [];
  for (const v of voxels) {
    if (!Array.isArray(v) || v.length < 3) continue;
    const t = [Number(v[0]), Number(v[1]), Number(v[2])] as const;
    if (!t.every(Number.isFinite)) continue;
    const id = t.join(",");
    if (seen.has(id)) continue;
    seen.add(id);
    out.push(t);
  }
  return out;
}

/**
 * Faces of unit voxels [x, y, z] in painter's order, with faces hidden by a
 * neighbouring voxel culled, fitted into the 100×100 box with `pad` margin.
 */
export function isoLayout(voxels: readonly (readonly unknown[])[], pad = 14): IsoLayout {
  const valid = validVoxels(Array.isArray(voxels) ? voxels : []);
  if (valid.length === 0) return { faces: [], unit: 0 };

  const occupied = new Set(valid.map((v) => v.join(",")));
  const has = (x: number, y: number, z: number) => occupied.has(`${x},${y},${z}`);
  const sorted = [...valid].sort(isoOrder);

  const raw: { key: string; face: IsoFaceName; voxel: Voxel; pts: Pt[] }[] = [];
  for (const voxel of sorted) {
    const [x, y, z] = voxel;
    const id = `${x},${y},${z}`;
    if (!has(x, y + 1, z)) {
      raw.push({
        key: `${id}l`,
        face: "left",
        voxel,
        pts: [isoProject(x, y + 1, z), isoProject(x + 1, y + 1, z), isoProject(x + 1, y + 1, z + 1), isoProject(x, y + 1, z + 1)],
      });
    }
    if (!has(x + 1, y, z)) {
      raw.push({
        key: `${id}r`,
        face: "right",
        voxel,
        pts: [isoProject(x + 1, y, z), isoProject(x + 1, y + 1, z), isoProject(x + 1, y + 1, z + 1), isoProject(x + 1, y, z + 1)],
      });
    }
    if (!has(x, y, z + 1)) {
      raw.push({
        key: `${id}t`,
        face: "top",
        voxel,
        pts: [isoProject(x, y, z + 1), isoProject(x + 1, y, z + 1), isoProject(x + 1, y + 1, z + 1), isoProject(x, y + 1, z + 1)],
      });
    }
  }

  const all = raw.flatMap((r) => r.pts);
  const minX = Math.min(...all.map((p) => p[0]));
  const maxX = Math.max(...all.map((p) => p[0]));
  const minY = Math.min(...all.map((p) => p[1]));
  const maxY = Math.max(...all.map((p) => p[1]));
  const span = BOX - pad * 2;
  const scale = span / Math.max(maxX - minX, maxY - minY, 1e-6);
  const offX = pad + (span - (maxX - minX) * scale) / 2;
  const offY = pad + (span - (maxY - minY) * scale) / 2;

  return {
    unit: scale,
    faces: raw.map((r) => ({
      key: r.key,
      face: r.face,
      voxel: r.voxel,
      points: r.pts.map(([px, py]) => `${f(offX + (px - minX) * scale)},${f(offY + (py - minY) * scale)}`).join(" "),
    })),
  };
}

/** Faces only (see `isoLayout`). */
export function isoFaces(voxels: readonly (readonly unknown[])[], pad = 14): IsoFace[] {
  return isoLayout(voxels, pad).faces;
}

/** Lattice positions for glyph.dots@1. */
export function dotLattice(rows: number, cols: number): { pitch: number; radius: number; at: (r: number, c: number) => Pt } {
  const n = Math.max(rows, cols, 1);
  const pitch = n <= 1 ? 0 : Math.min(15, 64 / (n - 1));
  const radius = n <= 3 ? 4.2 : Math.max(2.2, Math.min(4.2, pitch * 0.3));
  return {
    pitch,
    radius,
    at: (r, c) => [C + (c - (cols - 1) / 2) * pitch, C + (r - (rows - 1) / 2) * pitch],
  };
}

// ---------------------------------------------------------------- glyph.jug@1

export interface JugDrawing {
  /** Left edge of the inner body. */
  x: number;
  /** Top of the inner body (bottoms are aligned). */
  top: number;
  bottom: number;
  width: number;
  capacity: number;
  fill: number;
  /** Height of the water column. */
  water: number;
  /** One tick per litre above the floor, below the brim; `long` every 5 L. */
  ticks: { y: number; long: boolean }[];
}

export interface JugLayout {
  jugs: JugDrawing[];
  viewBox: { width: number; height: number };
  /** Units per litre (shared by every jug of the group). */
  litre: number;
  /** Space reserved above the tallest jug and below every jug for the labels. */
  labelBand: number;
}

/** Inner height of the tallest jug, in viewBox units; every jug is half this wide (so the largest is 1 : 2). */
export const JUG_TALLEST = 100;
export const JUG_WIDTH = JUG_TALLEST / 2;
const JUG_GAP = 26;
const JUG_PAD = 5;
const JUG_BAND = 17;

const posInt = (v: unknown, fallback: number, max = Number.POSITIVE_INFINITY) => {
  const n = typeof v === "number" && Number.isFinite(v) ? Math.round(v) : fallback;
  return Math.min(max, Math.max(0, n));
};

/**
 * One scale for a group of jugs (contracts/phase5.md, content/README "glyph.jug"):
 * the same width for all (half the tallest jug's inner height), inner heights
 * proportional to capacity, bottoms aligned, a tick every litre (longer every
 * 5 L), room for the fill label above and the capacity label below each jug.
 */
export function jugLayout(jugs: readonly { capacity?: unknown; fill?: unknown }[]): JugLayout {
  const list = jugs.map((j) => {
    const capacity = Math.max(1, posInt(j?.capacity, 1, 99));
    return { capacity, fill: Math.min(capacity, posInt(j?.fill, 0)) };
  });
  const maxCap = Math.max(1, ...list.map((j) => j.capacity));
  const litre = JUG_TALLEST / maxCap;
  const bottom = JUG_BAND + JUG_TALLEST;
  const drawn = list.map((j, i): JugDrawing => {
    const height = j.capacity * litre;
    const ticks: JugDrawing["ticks"] = [];
    for (let l = 1; l < j.capacity; l++) ticks.push({ y: f(bottom - l * litre), long: l % 5 === 0 });
    return {
      x: JUG_PAD + i * (JUG_WIDTH + JUG_GAP),
      top: f(bottom - height),
      bottom,
      width: JUG_WIDTH,
      capacity: j.capacity,
      fill: j.fill,
      water: f(j.fill * litre),
      ticks,
    };
  });
  const n = Math.max(1, drawn.length);
  return {
    jugs: drawn,
    litre,
    labelBand: JUG_BAND,
    viewBox: { width: JUG_PAD * 2 + n * JUG_WIDTH + (n - 1) * JUG_GAP, height: bottom + JUG_BAND },
  };
}

// ---------------------------------------------------------------- board.graph@1

/** The field's width in viewBox units; its height is `aspect` times this. */
export const GRAPH_WIDTH = 320;
/** Default field height ÷ width (content/README; road maps use 0.7, the board's 320 × 220). */
export const GRAPH_ASPECT = 1;
const NODE_R = 15;
const NODE_R_MIN = 9;
/** Road stroke width. */
export const GRAPH_ROAD = 2;
const PILL_H = 20;
const PILL_FONT = 11;
/** A short road's pill, tried on the road before a full one goes beside it (a flat map's close towns). */
const PILL_H_COMPACT = 16;
const PILL_FONT_COMPACT = 10;
const ARROW_LEN = 8;
const ARROW_HALF = 4.5;
/** Two roads between the same towns run in lanes this far apart. */
const LANE = 6;
/** The least gap between a weight label and a town, another road, another label or the field edge. */
export const GRAPH_CLEAR = 2;
/** Where along a road (centre to centre) a weight may sit, best first: the middle, then out to 0.2 / 0.8 in 0.03 steps. */
const ALONG = [0.5, ...Array.from({ length: 10 }, (_, i) => [0.5 - 0.03 * (i + 1), 0.5 + 0.03 * (i + 1)]).flat()].map((t) => Math.round(t * 100) / 100);

export type GraphRole = "start" | "goal" | "none";

export interface GraphNodeDrawing {
  id: string;
  label: string;
  x: number;
  y: number;
  role: GraphRole;
  fontSize: number;
}

/** A weight label: a pill centred on (x, y), axis-aligned so the text stays level. */
export interface GraphPill {
  x: number;
  y: number;
  width: number;
  height: number;
  fontSize: number;
}

export interface GraphEdgeDrawing {
  from: string;
  to: string;
  /** The towns' drawn labels. */
  fromLabel: string;
  toLabel: string;
  /** The visible road: from one town's rim to the other's (or to the arrowhead's base). */
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  directed: boolean;
  /** Tip, then the two back corners, when directed. */
  arrow: [Pt, Pt, Pt] | null;
  weight: string | null;
  pill: GraphPill | null;
  /**
   * `road`: on the road's middle, as on the board; `slide`: further along its own road (either may be a
   * compact pill, 16 high, on a road too short for a full one); `beside`: offset perpendicular to it,
   * touching distance or one pill further; `forced`: nowhere was clear, the least bad spot.
   */
  placement: "road" | "slide" | "beside" | "forced" | null;
}

export interface GraphLayout {
  /** The field, in viewBox units: node x, y are fractions of these. */
  width: number;
  height: number;
  /** The field plus a margin on every side when a town near the edge would otherwise be clipped. */
  viewBox: { x: number; y: number; width: number; height: number };
  aspect: number;
  nodeRadius: number;
  nodes: GraphNodeDrawing[];
  edges: GraphEdgeDrawing[];
}

interface Rect {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

const unit = (v: unknown, fallback: number) => (typeof v === "number" && Number.isFinite(v) ? Math.min(1, Math.max(0, v)) : fallback);
const idOf = (v: unknown) => (typeof v === "string" ? v.trim() : typeof v === "number" && Number.isFinite(v) ? String(v) : "");

/** A weight as drawn: numbers as written, strings trimmed; nothing for an empty one. */
export function graphWeight(v: unknown): string | null {
  if (typeof v === "number" && Number.isFinite(v)) return String(v);
  if (typeof v === "string" && v.trim()) return v.trim();
  return null;
}

function pointRectDistance(p: Pt, r: Rect): number {
  const dx = Math.max(r.x0 - p[0], 0, p[0] - r.x1);
  const dy = Math.max(r.y0 - p[1], 0, p[1] - r.y1);
  return Math.hypot(dx, dy);
}

function pointSegmentDistance(p: Pt, a: Pt, b: Pt): number {
  const vx = b[0] - a[0];
  const vy = b[1] - a[1];
  const len2 = vx * vx + vy * vy;
  const t = len2 > 0 ? Math.min(1, Math.max(0, ((p[0] - a[0]) * vx + (p[1] - a[1]) * vy) / len2)) : 0;
  return Math.hypot(p[0] - (a[0] + t * vx), p[1] - (a[1] + t * vy));
}

/** Liang–Barsky: does any part of the segment lie inside the rectangle? */
function segmentHitsRect(a: Pt, b: Pt, r: Rect): boolean {
  let t0 = 0;
  let t1 = 1;
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  const edges: [number, number][] = [
    [-dx, a[0] - r.x0],
    [dx, r.x1 - a[0]],
    [-dy, a[1] - r.y0],
    [dy, r.y1 - a[1]],
  ];
  for (const [p, q] of edges) {
    if (p === 0) {
      if (q < 0) return false;
    } else {
      const t = q / p;
      if (p < 0) t0 = Math.max(t0, t);
      else t1 = Math.min(t1, t);
      if (t0 > t1) return false;
    }
  }
  return true;
}

/** The distance between a segment and a rectangle (0 when they touch). */
export function segmentRectDistance(a: Pt, b: Pt, r: Rect): number {
  if (segmentHitsRect(a, b, r)) return 0;
  const corners: Pt[] = [
    [r.x0, r.y0],
    [r.x1, r.y0],
    [r.x1, r.y1],
    [r.x0, r.y1],
  ];
  return Math.min(pointRectDistance(a, r), pointRectDistance(b, r), ...corners.map((c) => pointSegmentDistance(c, a, b)));
}

function rectGap(a: Rect, b: Rect): number {
  const dx = Math.max(a.x0 - b.x1, 0, b.x0 - a.x1);
  const dy = Math.max(a.y0 - b.y1, 0, b.y0 - a.y1);
  return dx > 0 || dy > 0 ? Math.hypot(dx, dy) : -Math.min(a.x1 - b.x0, b.x1 - a.x0, a.y1 - b.y0, b.y1 - a.y0);
}

export const pillRect = (p: { x: number; y: number; width: number; height: number }): Rect => ({
  x0: p.x - p.width / 2,
  y0: p.y - p.height / 2,
  x1: p.x + p.width / 2,
  y1: p.y + p.height / 2,
});

/**
 * board.graph@1 (Road Map, board 19 P5) in a field GRAPH_WIDTH wide and `aspect` times as tall (a node's
 * x, y are fractions of its width and height, as board.scatter). Towns are circles (radius 15, smaller when
 * towns sit close; the view widens when one near an edge would be clipped);
 * roads are straight, from rim to rim, with an arrowhead at `to` when directed (two roads between the
 * same towns run in parallel lanes). Each weight sits on its road in a pill: at the middle when that is
 * clear, else slid along the road, else as a compact pill on the road (a short road), else offset
 * perpendicular to it, so it never covers a town, another road, another label or the edge. The label with
 * the fewest clear spots goes first.
 * Unknown town ids, self-loops and duplicate towns are dropped. Pure: the web draws it as one SVG.
 */
export function graphLayout(board: { aspect?: unknown; nodes?: unknown; edges?: unknown }): GraphLayout {
  const aspect = typeof board?.aspect === "number" && Number.isFinite(board.aspect) ? Math.min(2, Math.max(0.5, board.aspect)) : GRAPH_ASPECT;
  const width = GRAPH_WIDTH;
  const height = f(width * aspect);
  const seen = new Set<string>();
  const nodes: GraphNodeDrawing[] = [];
  for (const raw of Array.isArray(board?.nodes) ? board.nodes : []) {
    const n = raw as { id?: unknown; label?: unknown; x?: unknown; y?: unknown; role?: unknown } | null;
    const id = idOf(n?.id);
    if (!id || seen.has(id)) continue;
    seen.add(id);
    const label = typeof n?.label === "string" && n.label.trim() ? n.label.trim() : id;
    const role: GraphRole = n?.role === "start" || n?.role === "goal" ? n.role : "none";
    nodes.push({ id, label, x: f(unit(n?.x, 0.5) * width), y: f(unit(n?.y, 0.5) * height), role, fontSize: 0 });
  }
  let closest = Number.POSITIVE_INFINITY;
  for (let i = 0; i < nodes.length; i++)
    for (let j = i + 1; j < nodes.length; j++) closest = Math.min(closest, Math.hypot(nodes[i]!.x - nodes[j]!.x, nodes[i]!.y - nodes[j]!.y));
  const r = f(Math.min(NODE_R, Math.max(NODE_R_MIN, Number.isFinite(closest) ? closest * 0.38 : NODE_R)));
  // Generated maps keep towns clear of the edges; a hand-placed town at 0 or 1 widens the view instead of being clipped.
  const edge = Math.min(Number.POSITIVE_INFINITY, ...nodes.map((n) => Math.min(n.x, width - n.x, n.y, height - n.y)));
  const margin = Math.ceil(Math.max(0, r + 2 - (Number.isFinite(edge) ? edge : r + 2)));
  const viewBox = { x: margin ? -margin : 0, y: margin ? -margin : 0, width: width + 2 * margin, height: f(height + 2 * margin) };
  for (const n of nodes) {
    // Letters at 14 on a radius of 15 (the board); longer names shrink to fit the circle (~0.6 em a character).
    const chars = [...n.label].length;
    n.fontSize = f(Math.max(7, Math.min(r * 0.93, (2.6 * r) / Math.max(1, chars))));
  }
  const byId = new Map(nodes.map((n) => [n.id, n]));

  const raw = (Array.isArray(board?.edges) ? board.edges : [])
    .map((e) => e as { from?: unknown; to?: unknown; weight?: unknown; directed?: unknown } | null)
    .map((e) => ({ from: byId.get(idOf(e?.from)), to: byId.get(idOf(e?.to)), weight: graphWeight(e?.weight), directed: e?.directed === true }))
    .filter((e): e is { from: GraphNodeDrawing; to: GraphNodeDrawing; weight: string | null; directed: boolean } => Boolean(e.from && e.to) && e.from !== e.to);

  // Roads between the same two towns share a corridor: spread them into lanes.
  const pairKey = (a: string, b: string) => (a < b ? `${a}\u0000${b}` : `${b}\u0000${a}`);
  const lanes = new Map<string, number>();
  for (const e of raw) lanes.set(pairKey(e.from.id, e.to.id), (lanes.get(pairKey(e.from.id, e.to.id)) ?? 0) + 1);
  const laneSeen = new Map<string, number>();
  // The centre line (lane included) and its normal, kept aside for placing the weights.
  const axes: { P: Pt; Q: Pt; n: Pt }[] = [];

  const edges: GraphEdgeDrawing[] = raw.map((e) => {
    const key = pairKey(e.from.id, e.to.id);
    const count = lanes.get(key) ?? 1;
    const k = laneSeen.get(key) ?? 0;
    laneSeen.set(key, k + 1);
    // The lane normal is taken from the pair's canonical direction, so opposite roads split apart.
    const [ca, cb] = e.from.id < e.to.id ? [e.from, e.to] : [e.to, e.from];
    const clen = Math.hypot(cb.x - ca.x, cb.y - ca.y) || 1;
    const lane = (k - (count - 1) / 2) * LANE;
    const ox = (-(cb.y - ca.y) / clen) * lane;
    const oy = ((cb.x - ca.x) / clen) * lane;
    const P: Pt = [e.from.x + ox, e.from.y + oy];
    const Q: Pt = [e.to.x + ox, e.to.y + oy];
    const len = Math.hypot(Q[0] - P[0], Q[1] - P[1]) || 1;
    const u: Pt = [(Q[0] - P[0]) / len, (Q[1] - P[1]) / len];
    const n: Pt = [-u[1], u[0]];
    // Where the road meets a rim (a lane off the centre line meets it a little earlier).
    const rim = Math.sqrt(Math.max(0, r * r - lane * lane));
    const start: Pt = [P[0] + u[0] * rim, P[1] + u[1] * rim];
    let end: Pt = [Q[0] - u[0] * rim, Q[1] - u[1] * rim];
    let arrow: [Pt, Pt, Pt] | null = null;
    if (e.directed) {
      const tipBack = Math.sqrt(Math.max(0, (r + 1.5) ** 2 - lane * lane));
      const tip: Pt = [Q[0] - u[0] * tipBack, Q[1] - u[1] * tipBack];
      const base: Pt = [tip[0] - u[0] * ARROW_LEN, tip[1] - u[1] * ARROW_LEN];
      arrow = [
        [f(tip[0]), f(tip[1])],
        [f(base[0] + n[0] * ARROW_HALF), f(base[1] + n[1] * ARROW_HALF)],
        [f(base[0] - n[0] * ARROW_HALF), f(base[1] - n[1] * ARROW_HALF)],
      ];
      end = base;
    }
    axes.push({ P, Q, n });
    return {
      from: e.from.id,
      to: e.to.id,
      fromLabel: e.from.label,
      toLabel: e.to.label,
      x1: f(start[0]),
      y1: f(start[1]),
      x2: f(end[0]),
      y2: f(end[1]),
      directed: e.directed,
      arrow,
      weight: e.weight,
      pill: null,
      placement: null,
    };
  });

  placeWeights(edges, axes, nodes, r, viewBox);
  return { width, height: f(height), viewBox, aspect, nodeRadius: r, nodes, edges };
}

function placeWeights(
  edges: GraphEdgeDrawing[],
  axes: readonly { P: Pt; Q: Pt; n: Pt }[],
  nodes: readonly GraphNodeDrawing[],
  r: number,
  view: { x: number; y: number; width: number; height: number },
) {
  type Candidate = { x: number; y: number; w: number; h: number; font: number; rect: Rect; t: number; tier: number; penalty: number; clearance: number };
  // Every spot a label may take, scored against what never moves: the view's edge, the towns, the roads.
  const spots = edges.map((e, i): Candidate[] | null => {
    if (e.weight === null) return null;
    const chars = [...e.weight].length;
    const { P, Q, n } = axes[i]!;
    const candidates: Candidate[] = [];
    // Tiers, best first: a full pill on the road's middle (0) or slid along it (1); a compact pill on the
    // road, allowed within 1 of its own towns' rims (2); a full pill beside the road (3); further out (4).
    const full = { w: Math.max(PILL_H, chars * 6.4 + 9), h: PILL_H, font: PILL_FONT, own: GRAPH_CLEAR };
    const compact = { w: Math.max(PILL_H_COMPACT, chars * 5.8 + 7), h: PILL_H_COMPACT, font: PILL_FONT_COMPACT, own: 1 };
    const beside = Math.abs(n[0]) * (full.w / 2) + Math.abs(n[1]) * (full.h / 2) + GRAPH_ROAD / 2 + GRAPH_CLEAR + 1;
    const far = beside + full.h / 2 + GRAPH_CLEAR;
    for (const [tierBase, size, offsets] of [
      [0, full, [0]],
      [2, compact, [0]],
      [3, full, [beside, -beside]],
      [4, full, [far, -far]],
    ] as const) {
      const { w, h, font, own } = size;
      for (const t of ALONG) {
        for (const s of offsets) {
          const x = P[0] + (Q[0] - P[0]) * t + n[0] * s;
          const y = P[1] + (Q[1] - P[1]) * t + n[1] * s;
          const rect = pillRect({ x, y, width: w, height: h });
          // How far each obstacle is beyond the distance it needs (negative = overlap).
          const margins: number[] = [rect.x0 - view.x, rect.y0 - view.y, view.x + view.width - rect.x1, view.y + view.height - rect.y1];
          for (const node of nodes) {
            const gap = node.id === e.from || node.id === e.to ? own : GRAPH_CLEAR;
            margins.push(pointRectDistance([node.x, node.y], rect) - (r + 1 + gap));
          }
          for (const other of edges) {
            const d = segmentRectDistance([other.x1, other.y1], [other.x2, other.y2], rect);
            if (other === e) {
              if (s !== 0) margins.push(d - (GRAPH_ROAD / 2 + 1));
              continue;
            }
            margins.push(d - (GRAPH_ROAD / 2 + GRAPH_CLEAR));
            if (other.arrow) {
              const base: Pt = [(other.arrow[1][0] + other.arrow[2][0]) / 2, (other.arrow[1][1] + other.arrow[2][1]) / 2];
              margins.push(segmentRectDistance(other.arrow[0], base, rect) - (ARROW_HALF + GRAPH_CLEAR));
            }
          }
          const penalty = margins.reduce((sum, m) => sum + Math.max(0, -m), 0);
          const tier = tierBase === 0 ? (t === 0.5 ? 0 : 1) : tierBase;
          candidates.push({ x, y, w, h, font, rect, t, tier, penalty, clearance: Math.min(...margins) });
        }
      }
    }
    return candidates;
  });

  // Then the labels themselves: the one with the fewest clear spots left goes first (ties: the shorter
  // road), so a crowded road isn't boxed in by a neighbour that had room to spare.
  const placed: Rect[] = [];
  const free = (c: Candidate) => c.penalty < 1e-6 && placed.every((p) => rectGap(c.rect, p) >= GRAPH_CLEAR);
  const todo = new Set(spots.flatMap((spot, i) => (spot ? [i] : [])));
  const length = (i: number) => Math.hypot(edges[i]!.x2 - edges[i]!.x1, edges[i]!.y2 - edges[i]!.y1);
  while (todo.size) {
    let next = -1;
    let fewest = Number.POSITIVE_INFINITY;
    for (const i of todo) {
      const count = spots[i]!.filter(free).length;
      if (count < fewest || (count === fewest && length(i) < length(next))) {
        next = i;
        fewest = count;
      }
    }
    todo.delete(next);
    const candidates = spots[next]!;
    const clear = candidates.filter(free);
    // The first clear tier; inside it the spot nearest the middle, then the roomiest.
    let best: Candidate;
    if (clear.length) {
      const tier = Math.min(...clear.map((c) => c.tier));
      // (0.47 and 0.53 are equally near the middle: a float tolerance lets the roomier one win, as on iOS.)
      const nearer = (a: Candidate, b: Candidate) => Math.abs(a.t - 0.5) - Math.abs(b.t - 0.5);
      best = clear.filter((c) => c.tier === tier).sort((a, b) => (Math.abs(nearer(a, b)) > 1e-9 ? nearer(a, b) : b.clearance - a.clearance))[0]!;
    } else {
      const overlap = (c: Candidate) => c.penalty + placed.reduce((sum, p) => sum + Math.max(0, GRAPH_CLEAR - rectGap(c.rect, p)), 0);
      best = [...candidates].sort((a, b) =>
        Math.abs(overlap(a) - overlap(b)) > 1e-9 ? overlap(a) - overlap(b) : a.tier - b.tier || Math.abs(a.t - 0.5) - Math.abs(b.t - 0.5),
      )[0]!;
    }
    const e = edges[next]!;
    e.pill = { x: f(best.x), y: f(best.y), width: f(best.w), height: best.h, fontSize: best.font };
    e.placement = !clear.length ? "forced" : best.tier === 0 || (best.tier === 2 && best.t === 0.5) ? "road" : best.tier <= 2 ? "slide" : "beside";
    placed.push(pillRect(e.pill));
  }
}
